import * as Sentry from "@sentry/react";

import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import {
  selectDepartmentId,
  selectIsLoggedIn,
  selectUserInfo,
} from "../../../src/redux/app";
import { useAppDispatch, useAppSelector } from "../../../src/redux/hooks";
import {
  useCustomerIoAnalytics,
  useRudderStackAnalytics,
} from "../useAnalytics";

// Regular imports
import { CAREERS_URL } from "./landing_pages/navigation/config/navConfig";
import CheckYourEmail from "./CheckYourEmail";
import ContactPage from "./landing_pages/ContactPage";
import ContactUs from "./ContactUs";
// Core components and imports
import ErrorPage from "./ErrorPage";
import Footer from "./Footer";
import { GET_DEPARTMENT_ORDER_PREFERENCES } from "./provider_order_forms/OrderFormQueries";
import GoLinkLogin from "./login/GoLinkLogin";
import HomePage from "./HomePage";
import JetLensesHomePage from "./JetLensesHomePage";
import { Layout } from "./landing_pages/Layout";
import { Loading } from "@verse_core/shared_components/base/Loading";
import Login from "./Login";
import LoginPage from "./LoginPage";
import NewHomePage from "./landing_pages/NewHomePage";
import NewLoginPage from "./login/NewLoginPage";
import OrderSigned from "./OrderSigned";
import OrderUpdateRequested from "./OrderUpdateRequested";
import PasswordResetLinkSent from "./PasswordResetLinkSent";
import PatientRights from "./PatientRights";
import ProviderNavigation from "./ProviderNavigation";
import ProviderSignupPage from "./signup/ProviderSignupPage";
import RequestPasswordReset from "./RequestPasswordReset";
import ResetPassword from "./ResetPassword";
import ScopeOfServicePage from "./ScopeOfServicePage";
import SignatureReceived from "./Signatures/SignatureReceived";
import StandAloneIssueResponse from "./StandAloneIssueResponse";
import Terms from "./Terms";
import { Toaster } from "sonner";
import { isPatientPortalPath } from "../isPatientPortalPath";
import { rebrandEnabled } from "./util/brandingFlag";
import { routes } from "./routes/config";
import { setDepartmentOrderPreferences } from "../../../src/redux/department";
import { signupEnabled } from "./util/signupFlag";
import useAuth from "../../../src/auth/useAuth";
import { useLazyQuery } from "@apollo/client";
import { useRouteTitle } from "./util/documentTitle";
import { useVersionCheck } from "./util/routerUtil";

const VISIBLE_TOASTS = 5;

const ExternalRedirect = ({ url }: { url: string }) => {
  useEffect(() => {
    window.location.replace(url);
  }, [url]);

  return <Loading fullscreen />;
};

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Lazy load components with PDF dependencies
const HandOffPartnerPortal = lazy(
  () => import("./hand_off_partner_portal/HandOffPartnerPortal"),
);
const ManufacturerOrderList = lazy(() => import("./ManufacturerOrderList"));
const ProviderPatientCheckInRequest = lazy(
  () => import("./ProviderPatientCheckInRequest"),
);
const ProviderPortal = lazy(() => import("./ProviderPortal"));
const PatientPortal = lazy(() => import("./patient_portal/PatientPortal"));
const PayorMembersPortal = lazy(
  () => import("./payor_members/PayorMembersPortal"),
);
const AnalyticsPortal = lazy(
  () => import("./analytics_portal/AnalyticsPortal"),
);

const WithProviderNavigation = ({
  children,
  showOldNav,
}: {
  children: React.ReactNode;
  showOldNav: boolean;
}) => (
  <>
    <ProviderNavigation showOldNav={showOldNav} />
    {children}
  </>
);

/** Password and magic-link screens: the rebranded layout under the flag, else the old nav. */
const WithPasswordLayout = ({ children }: { children: React.ReactNode }) =>
  rebrandEnabled() ? (
    <Layout>{children}</Layout>
  ) : (
    <WithProviderNavigation showOldNav>{children}</WithProviderNavigation>
  );

export default function App() {
  const dispatch = useAppDispatch();
  const selectedDepartmentId = useAppSelector(selectDepartmentId);

  const currentUserInfo = useAppSelector(selectUserInfo);

  const [getDepartmentOrderPreferences] = useLazyQuery(
    GET_DEPARTMENT_ORDER_PREFERENCES,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (data?.departmentOrderPreferences) {
          dispatch(
            setDepartmentOrderPreferences(data.departmentOrderPreferences),
          );
        }
      },
    },
  );

  /*
  Update the department order preferences when the selected department id changes.
  Particularly needed for when the user switches locations.
  */
  useEffect(() => {
    if (selectedDepartmentId) {
      getDepartmentOrderPreferences({
        variables: { departmentId: selectedDepartmentId },
      });
    }
  }, [selectedDepartmentId]);

  const { authIsLoading } = useAuth();

  const location = useLocation();
  const analytics = useRudderStackAnalytics();
  const customerIoAnalytics = useCustomerIoAnalytics();
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  useVersionCheck();
  useRouteTitle();

  useEffect(() => {
    // Fire page() on every route change. Customer.io in-app page rules match
    // on these page() calls, so it must fire per SPA navigation (React Router
    // v6) -- but never on the portal, which names its own pages.
    if (!isPatientPortalPath(location.pathname)) {
      analytics?.page(location.pathname);
      customerIoAnalytics?.page(location.pathname);
    }
  }, [location]);

  // check if user is logged in, or if the user is not logged in and the loading is done
  return isLoggedIn || !authIsLoading ? (
    <>
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <Toaster
        position="top-center"
        richColors
        expand={false}
        visibleToasts={VISIBLE_TOASTS}
      />
      <div>
        <div className="tw-flex tw-h-screen tw-flex-col tw-justify-between">
          <>
            <div className="tw-flex tw-h-fit tw-flex-col">
              <Suspense fallback={<Loading fullscreen />}>
                <SentryRoutes>
                  <Route
                    path="/"
                    element={
                      isLoggedIn ? (
                        <HomePage />
                      ) : (
                        <Layout>
                          <NewHomePage />
                        </Layout>
                      )
                    }
                  />
                  <Route
                    path="contact"
                    element={
                      <Layout>
                        <ContactPage />
                      </Layout>
                    }
                  />
                  <Route
                    path="jetlenses"
                    element={
                      <WithProviderNavigation showOldNav>
                        <JetLensesHomePage />
                      </WithProviderNavigation>
                    }
                  />
                  <Route
                    path="payors"
                    element={<Navigate to="/payor-network" replace />}
                  />
                  <Route
                    path="about_us"
                    element={<Navigate to="/about" replace />}
                  />
                  <Route
                    path="terms"
                    element={
                      <WithProviderNavigation showOldNav>
                        <Terms />
                      </WithProviderNavigation>
                    }
                  />
                  <Route
                    path="patient-rights"
                    element={
                      <WithProviderNavigation showOldNav>
                        <PatientRights />
                      </WithProviderNavigation>
                    }
                  />
                  <Route
                    path="careers"
                    element={<ExternalRedirect url={CAREERS_URL} />}
                  />
                  <Route
                    path="sign-up"
                    element={
                      signupEnabled() ? (
                        <Layout>
                          <ProviderSignupPage />
                        </Layout>
                      ) : (
                        <Navigate to="/password-login?type=provider" replace />
                      )
                    }
                  />
                  <Route path="go-link-login" element={<GoLinkLogin />} />
                  <Route
                    path="password-login"
                    element={
                      rebrandEnabled() ? (
                        <Layout>
                          <NewLoginPage />
                        </Layout>
                      ) : isLoggedIn ? (
                        <LoginPage />
                      ) : (
                        <Layout>
                          <LoginPage />
                        </Layout>
                      )
                    }
                  />
                  <Route
                    path="login"
                    element={
                      rebrandEnabled() ? (
                        <Layout>
                          <NewLoginPage />
                        </Layout>
                      ) : isLoggedIn ? (
                        <Login />
                      ) : (
                        <Layout>
                          <Login />
                        </Layout>
                      )
                    }
                  />
                  <Route
                    path="password/reset-link-sent"
                    element={
                      <WithPasswordLayout>
                        <PasswordResetLinkSent />
                      </WithPasswordLayout>
                    }
                  />
                  <Route
                    path="password/request-password-reset"
                    element={
                      <WithPasswordLayout>
                        <RequestPasswordReset />
                      </WithPasswordLayout>
                    }
                  />
                  <Route
                    path="password/reset"
                    element={
                      <WithPasswordLayout>
                        <ResetPassword />
                      </WithPasswordLayout>
                    }
                  />
                  <Route
                    path="contact_us"
                    element={
                      isLoggedIn ? (
                        <WithProviderNavigation showOldNav>
                          <ContactUs />
                        </WithProviderNavigation>
                      ) : (
                        <Layout>
                          <ContactPage />
                        </Layout>
                      )
                    }
                  />
                  <Route
                    path="check-your-email"
                    element={
                      <WithPasswordLayout>
                        <CheckYourEmail />
                      </WithPasswordLayout>
                    }
                  />
                  <Route
                    path="provider_orders/order-signed/:orderNumber"
                    element={
                      <WithProviderNavigation showOldNav>
                        <OrderSigned />
                      </WithProviderNavigation>
                    }
                  />
                  <Route
                    path="provider_orders/order-update-request/new"
                    element={
                      <WithProviderNavigation showOldNav>
                        <OrderUpdateRequested />
                      </WithProviderNavigation>
                    }
                  />
                  <Route
                    path="provider_orders/stand_alone_issue_response"
                    element={
                      <WithProviderNavigation showOldNav>
                        <StandAloneIssueResponse />
                      </WithProviderNavigation>
                    }
                  />
                  <Route
                    path="provider_orders/patient-check-in-request"
                    element={
                      <WithProviderNavigation showOldNav>
                        <ProviderPatientCheckInRequest />
                      </WithProviderNavigation>
                    }
                  />
                  <Route
                    path="provider_orders/signature-received"
                    element={
                      <WithProviderNavigation showOldNav>
                        <SignatureReceived />
                      </WithProviderNavigation>
                    }
                  />

                  <Route
                    path="provider_order_list/*"
                    element={
                      <WithProviderNavigation showOldNav={false}>
                        <ProviderPortal />
                      </WithProviderNavigation>
                    }
                  />
                  <Route
                    path="provider_orders/*"
                    element={
                      <WithProviderNavigation showOldNav={false}>
                        <ProviderPortal />
                      </WithProviderNavigation>
                    }
                  />
                  <Route
                    path="provider-settings/*"
                    element={
                      <WithProviderNavigation showOldNav={false}>
                        <ProviderPortal />
                      </WithProviderNavigation>
                    }
                  />

                  <Route
                    path="members/*"
                    element={
                      <WithProviderNavigation showOldNav={false}>
                        <PayorMembersPortal />
                      </WithProviderNavigation>
                    }
                  />

                  <Route
                    path="manufacturer_order_list/*"
                    element={
                      <WithProviderNavigation showOldNav>
                        <ManufacturerOrderList
                          userEmail={currentUserInfo.email ?? ""}
                          providerName={
                            currentUserInfo.userDepartments[0]?.hospitalName ??
                            ""
                          }
                          providerId={
                            currentUserInfo.userDepartments[0]?.hospitalId ?? 0
                          }
                        />
                      </WithProviderNavigation>
                    }
                  />

                  <Route
                    path="hand_off_partner_order_list/*"
                    element={
                      <WithProviderNavigation showOldNav>
                        <HandOffPartnerPortal />
                      </WithProviderNavigation>
                    }
                  />

                  <Route
                    path="analytics/*"
                    element={
                      <WithProviderNavigation showOldNav={false}>
                        <AnalyticsPortal />
                      </WithProviderNavigation>
                    }
                  />

                  <Route
                    path="scope-of-service/:accessToken"
                    element={<ScopeOfServicePage />}
                  />

                  <Route path="patient-portal/*" element={<PatientPortal />} />

                  <Route
                    path="/telehealth"
                    element={<Navigate to="/" replace />}
                  />

                  {routes
                    .filter((route) => route.path !== "*")
                    .map((route) => {
                      const Component = route.component;
                      return (
                        <Route
                          key={route.path}
                          path={route.path}
                          element={
                            <Layout>
                              <Component />
                            </Layout>
                          }
                        />
                      );
                    })}
                  <Route path="*" element={<ErrorPage variant="not-found" />} />
                </SentryRoutes>
              </Suspense>
            </div>
            {location?.pathname !== "/jetlenses" &&
              !isPatientPortalPath(location.pathname) &&
              isLoggedIn && <Footer />}
          </>
        </div>
      </div>
    </>
  ) : null;
}
